import { ApolloQueryResult } from '@apollo/client';
import { createContext, useContext } from 'react';

import { MessageMedium } from 'modules/graphql/types';
import noop, { NoOp } from 'utils/noop';

import { DEFAULT_EDIT_SINGLE_ROW_STATE } from 'modules/templateEditor/context/EditSavedRowDesignContext';
import { QuerySavedRowsResponse, QuerySavedRowsVariables } from '../../hooks';
import { ManageMessageCategoriesMutationFunction } from '../../hooks/useManageMessageCategories';
import { QueryMessageCategoriesVariables } from '../../hooks/useQueryMessageCategories';
import { UseSimulateParseBeeRowQueryReturnType } from '../../hooks/useSimulateParseBeeRowQuery';
import { UpdateSavedRowVariables } from '../../hooks/useUpdateSavedRowMutation';
import {
  AuthToken,
  CreateSavedRowResponse,
  DeleteSavedRowResponse,
  DeleteSavedRowVariables,
  DragAndDropEditorProps,
  MessageCategory,
  SavedRow,
  TrackErrorConfig,
  UpdateSavedRowResponse,
  UpsertSavedRowVariables,
} from '../../types';

export type DragAndDropEditorContextState = {
  authToken?: AuthToken;
  authTokenError: unknown;
  getBeeAuthToken: NoOp;
  getMergeParams: (messageMedium?: MessageMedium) => void;
  isFetchingAuthToken: boolean;
  isFetchingMergeParams: boolean;
  mergeParams: string[];
  mergeParamsError: unknown;
  projectUID: string;
  trackError: (config: TrackErrorConfig) => void;

  // saved row data
  messageCategories: MessageCategory[];
  isFetchingCategories: boolean;
  getMessageCategories: (variables?: QueryMessageCategoriesVariables) => void;
  createSavedRow: ManageMessageCategoriesMutationFunction<
    CreateSavedRowResponse,
    UpsertSavedRowVariables
  >;
  isCreatingSavedRow: boolean;
  createSavedRowFailure: Record<string, unknown> | null;
  createSavedRowReset: NoOp;
  deleteSavedRow: ManageMessageCategoriesMutationFunction<
    DeleteSavedRowResponse,
    DeleteSavedRowVariables
  >;
  isDeletingSavedRow: boolean;
  deleteSavedRowFailure: Record<string, unknown> | null;
  savedRows: SavedRow[];
  getSavedRows: (
    variables: QuerySavedRowsVariables,
  ) => Promise<ApolloQueryResult<QuerySavedRowsResponse> | undefined>;
  isFetchingSavedRows: boolean;
  savedRowsError: unknown;
  updateSavedRow: ManageMessageCategoriesMutationFunction<
    UpdateSavedRowResponse,
    UpdateSavedRowVariables
  >;
  isUpdatingSavedRow: boolean;
  resetUpdateSavedRow: NoOp;
  updateSavedRowFailure: Record<string, unknown> | null;
  parseSavedRowProcess?: UseSimulateParseBeeRowQueryReturnType;
  getSavedRowCount: () => void;
  savedRowCount: number | null;
  isSavingTemplateForSavedRow: boolean;
  setIsSavingTemplateForSavedRow: (isSaving: boolean) => void;
} & Pick<
  DragAndDropEditorProps,
  'hasUnsavedTemplateChanges' | 'editSavedRowDesign' | 'messageMedium'
>;

const DEFAULT_DRAG_AND_DROP_EDITOR_CONTEXT_STATE: DragAndDropEditorContextState = {
  authToken: undefined,
  authTokenError: null,
  getBeeAuthToken: noop,
  getMergeParams: noop,
  isFetchingAuthToken: false,
  isFetchingMergeParams: false,
  mergeParams: [],
  mergeParamsError: null,
  projectUID: '',
  messageMedium: undefined as any,
  trackError: noop,

  // saved row data
  messageCategories: [],
  isFetchingCategories: false,
  getMessageCategories: noop,
  createSavedRow: noop,
  isCreatingSavedRow: false,
  createSavedRowFailure: {},
  createSavedRowReset: noop,
  deleteSavedRow: noop,
  isDeletingSavedRow: false,
  deleteSavedRowFailure: {},
  savedRows: [],
  getSavedRows: noop as any,
  isFetchingSavedRows: false,
  savedRowsError: null,
  updateSavedRow: noop,
  resetUpdateSavedRow: noop,
  isUpdatingSavedRow: false,
  updateSavedRowFailure: {},
  parseSavedRowProcess: undefined,
  getSavedRowCount: noop as any,
  savedRowCount: null,
  editSavedRowDesign: DEFAULT_EDIT_SINGLE_ROW_STATE,
  hasUnsavedTemplateChanges: false,
  isSavingTemplateForSavedRow: false,
  setIsSavingTemplateForSavedRow: noop,
};

export const DragAndDropEditorContext = createContext<DragAndDropEditorContextState>(
  DEFAULT_DRAG_AND_DROP_EDITOR_CONTEXT_STATE,
);

export const useDragAndDropEditorContext = () => useContext(DragAndDropEditorContext);
